'use client';

import React, { useState } from 'react';
import { X, Lock, ChevronRight, Sparkles } from 'lucide-react';

export interface Category {
  id: string;
  label: string;
  emoji: string;
  description: string;
  premium: boolean;
}

interface CategorySelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (category: Category) => void;
  onPremiumRequired: () => void;
  gameName: string;
  gameId: string;
  gameColor: string;
}

const GAME_CATEGORIES: Record<string, Category[]> = {
  'game-emoji-raten': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Gemischte Emoji-Rätsel für alle', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Filme, Serien und Charaktere', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Bands, Songs und Künstler', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportarten und Athleten', premium: false },
    { id: 'essen', label: 'Essen & Trinken', emoji: '🍕', description: 'Gerichte und Küchen der Welt', premium: false },
    { id: 'natur', label: 'Natur & Tiere', emoji: '🦁', description: 'Tiere und Naturphänomene', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Länder, Städte und Sehenswürdigkeiten', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Verrückte und schwierige Rätsel', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante Emoji-Rätsel 18+', premium: true },
  ],
  'game-wer-bin-ich': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Berühmte Persönlichkeiten aus allen Bereichen', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Filmcharaktere und Serienfiguren', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Berühmte Sportler', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musikstars und Künstler', premium: false },
    { id: 'geschichte', label: 'Geschichte', emoji: '📜', description: 'Historische Persönlichkeiten', premium: false },
    { id: 'tiere', label: 'Tiere', emoji: '🦁', description: 'Tierarten erraten', premium: false },
    { id: 'berufe', label: 'Berufe', emoji: '👷', description: 'Verschiedene Berufe', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Unerwartete und verrückte Personas', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante Personas 18+', premium: true },
  ],
  'game-impostor': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Alltägliche Begriffe für alle', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Themen rund ums Freundesleben', premium: false },
    { id: 'familie', label: 'Familie', emoji: '👨‍👩‍👧', description: 'Familiäre Begriffe & Situationen', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Serien, Filme und Stars', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportarten und Athleten', premium: false },
    { id: 'essen', label: 'Essen & Trinken', emoji: '🍕', description: 'Gerichte, Zutaten und Restaurants', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Bands, Songs und Instrumente', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Länder, Städte und Sehenswürdigkeiten', premium: false },
    { id: 'natur', label: 'Natur & Tiere', emoji: '🦁', description: 'Tiere, Pflanzen und Naturphänomene', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Verrückte und unerwartete Begriffe', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante und heiße Begriffe 18+', premium: true },
  ],
  'game-antwort-impostor': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Klassische Fragen für alle', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Fragen unter Freunden', premium: false },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante Fragen 18+', premium: true },
  ],
  'game-flasche': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Klassische Wahrheit oder Pflicht', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Fragen unter Freunden', premium: false },
    { id: 'kennenlernen', label: 'Kennenlernen', emoji: '🤝', description: 'Perfekt für neue Bekanntschaften', premium: false },
    { id: 'lustig', label: 'Lustig', emoji: '😂', description: 'Witzige Aufgaben und Fragen', premium: false },
    { id: 'sport', label: 'Sport & Fitness', emoji: '💪', description: 'Sportliche Herausforderungen', premium: false },
    { id: 'kreativ', label: 'Kreativ', emoji: '🎨', description: 'Kreative und künstlerische Aufgaben', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Fragen zu Filmen und Serien', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musikbezogene Aufgaben', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Unvorhersehbare Aufgaben', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Heiße Fragen und Aufgaben 18+', premium: true },
  ],
  'game-wahrheit-pflicht': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Klassische Fragen für alle', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Fragen unter Freunden', premium: false },
    { id: 'familie', label: 'Familie', emoji: '👨‍👩‍👧', description: 'Familienfreundliche Fragen', premium: false },
    { id: 'schule', label: 'Schule & Arbeit', emoji: '📚', description: 'Fragen rund um Alltag', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Abenteuer und Reiseerlebnisse', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Fragen zu Unterhaltung', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportliche Fragen', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musikbezogene Fragen', premium: false },
    { id: 'persoenlich', label: 'Persönlich', emoji: '💭', description: 'Tiefgründige persönliche Fragen', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Verrückte Aufgaben', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Heiße Fragen 18+', premium: true },
  ],
  'game-werwolf': [
    { id: 'klassisch', label: 'Klassisch', emoji: '🐺', description: 'Das originale Werwolf-Erlebnis', premium: false },
    { id: 'fantasy', label: 'Fantasy', emoji: '🧙', description: 'Magische Rollen und Kräfte', premium: false },
    { id: 'mittelalter', label: 'Mittelalter', emoji: '⚔️', description: 'Ritter, Hexen und Dämonen', premium: false },
    { id: 'modern', label: 'Modern', emoji: '🏙️', description: 'Stadtthema mit modernen Rollen', premium: false },
    { id: 'horror', label: 'Horror', emoji: '👻', description: 'Gruselige Rollen und Ereignisse', premium: false },
    { id: 'piraten', label: 'Piraten', emoji: '🏴‍☠️', description: 'Auf hoher See mit Piraten', premium: false },
    { id: 'weltraum', label: 'Weltraum', emoji: '🚀', description: 'Sci-Fi Rollen im All', premium: false },
    { id: 'mafia', label: 'Mafia', emoji: '🕶️', description: 'Gangster und Detektive', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Unvorhersehbare Sonderrollen', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Erwachsene Rollen 18+', premium: true },
  ],
  'game-quiz': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Fragen aus allen Bereichen', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Kino, Serien und Stars', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Bands, Songs und Charts', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportarten und Rekorde', premium: false },
    { id: 'wissenschaft', label: 'Wissenschaft', emoji: '🔬', description: 'Naturwissenschaften und Technik', premium: false },
    { id: 'geographie', label: 'Geographie', emoji: '🗺️', description: 'Länder, Städte und Flüsse', premium: false },
    { id: 'geschichte', label: 'Geschichte', emoji: '📜', description: 'Historische Ereignisse', premium: false },
    { id: 'essen', label: 'Essen & Kochen', emoji: '🍕', description: 'Gerichte und Küchen der Welt', premium: false },
    { id: 'natur', label: 'Natur & Tiere', emoji: '🦁', description: 'Tiere und Naturphänomene', premium: false },
    { id: 'chaos', label: 'Chaos-Quiz', emoji: '🌀', description: 'Absurde und verrückte Fragen', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante Wissensfragen 18+', premium: true },
  ],
  'game-busfahrer': [
    { id: 'klassisch', label: 'Klassisch', emoji: '🚌', description: 'Das originale Busfahrer-Spiel', premium: false },
    { id: 'schnell', label: 'Schnell & Intensiv', emoji: '⚡', description: 'Schnellere Runden', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Mit Freunden-Bonusrunden', premium: false },
    { id: 'familie', label: 'Familie', emoji: '👨‍👩‍👧', description: 'Familienfreundliche Variante', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportliche Bonusaufgaben', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Unterhaltungs-Bonusfragen', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musik-Bonusrunden', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Reise-Bonusfragen', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Verrückte Sonderregeln', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Heiße Bonusaufgaben 18+', premium: true },
  ],
  'game-kings-cup': [
    { id: 'klassisch', label: 'Klassisch', emoji: '👑', description: 'Originale King\'s Cup Regeln', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Angepasste Regeln für Freunde', premium: false },
    { id: 'lustig', label: 'Lustig', emoji: '😂', description: 'Witzige Sonderregeln', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportliche Herausforderungen', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Unterhaltungs-Regeln', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musik-Sonderregeln', premium: false },
    { id: 'kreativ', label: 'Kreativ', emoji: '🎨', description: 'Kreative Aufgaben', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Reise-Themen', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Chaotische Sonderregeln', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Heiße Regeln 18+', premium: true },
  ],
  'game-ich-hab-noch-nie': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Klassische Aussagen für alle', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Unter Freunden', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Reise-Erlebnisse', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportliche Erlebnisse', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Unterhaltungs-Erlebnisse', premium: false },
    { id: 'essen', label: 'Essen & Kochen', emoji: '🍕', description: 'Kulinarische Erlebnisse', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musik-Erlebnisse', premium: false },
    { id: 'abenteuer', label: 'Abenteuer', emoji: '🏔️', description: 'Mutige Erlebnisse', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Verrückte Erlebnisse', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante Erlebnisse 18+', premium: true },
  ],
  'game-bierpong': [
    { id: 'klassisch', label: 'Klassisch', emoji: '🏓', description: 'Originale Bier-Pong Regeln', premium: false },
    { id: 'turnier', label: 'Turnier', emoji: '🏆', description: 'Bracket-Turnier Modus', premium: false },
    { id: 'teams', label: 'Teams', emoji: '👥', description: 'Team-Variante', premium: false },
    { id: 'sport', label: 'Sport-Edition', emoji: '⚽', description: 'Sportliche Sonderregeln', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Unterhaltungs-Sonderregeln', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musik-Sonderregeln', premium: false },
    { id: 'schnell', label: 'Speed-Runde', emoji: '⚡', description: 'Schnellere Spielvariante', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Reise-Themen', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Chaotische Sonderregeln', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Heiße Sonderregeln 18+', premium: true },
  ],
  'game-pantomime': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Alltägliche Begriffe', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Filmtitel und Charaktere', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportarten und Bewegungen', premium: false },
    { id: 'tiere', label: 'Tiere', emoji: '🦁', description: 'Tiere und ihre Bewegungen', premium: false },
    { id: 'berufe', label: 'Berufe', emoji: '👷', description: 'Verschiedene Berufe', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musikstile und Instrumente', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Reiseziele und Aktivitäten', premium: false },
    { id: 'essen', label: 'Essen', emoji: '🍕', description: 'Gerichte und Zutaten', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Absurde und verrückte Begriffe', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante Begriffe 18+', premium: true },
  ],
  'game-hot-seat': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Allgemeine Fragen', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Fragen unter Freunden', premium: false },
    { id: 'familie', label: 'Familie', emoji: '👨‍👩‍👧', description: 'Familiäre Fragen', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Reise-Erlebnisse', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Unterhaltungs-Fragen', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportliche Fragen', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Musik-Fragen', premium: false },
    { id: 'karriere', label: 'Karriere & Träume', emoji: '🌟', description: 'Zukunft und Ambitionen', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Verrückte Fragen', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante Fragen 18+', premium: true },
  ],
  'default': [
    { id: 'allgemein', label: 'Allgemein', emoji: '🌍', description: 'Allgemeine Themen für alle', premium: false },
    { id: 'freunde', label: 'Freunde', emoji: '👫', description: 'Themen rund ums Freundesleben', premium: false },
    { id: 'familie', label: 'Familie', emoji: '👨‍👩‍👧', description: 'Familiäre Themen', premium: false },
    { id: 'film-tv', label: 'Film & TV', emoji: '🎬', description: 'Serien, Filme und Stars', premium: false },
    { id: 'sport', label: 'Sport', emoji: '⚽', description: 'Sportarten und Athleten', premium: false },
    { id: 'essen', label: 'Essen & Trinken', emoji: '🍕', description: 'Gerichte und Restaurants', premium: false },
    { id: 'musik', label: 'Musik', emoji: '🎵', description: 'Bands, Songs und Instrumente', premium: false },
    { id: 'reisen', label: 'Reisen', emoji: '✈️', description: 'Länder und Städte', premium: false },
    { id: 'natur', label: 'Natur & Tiere', emoji: '🦁', description: 'Tiere und Natur', premium: false },
    { id: 'chaos', label: 'Chaos', emoji: '🌀', description: 'Verrückte und unerwartete Themen', premium: true },
    { id: 'spicy', label: 'Spicy', emoji: '🌶️', description: 'Pikante Themen 18+', premium: true },
  ],
};

export function getCategoriesForGame(gameId: string): Category[] {
  return GAME_CATEGORIES[gameId] ?? GAME_CATEGORIES['default'];
}

export default function CategorySelectModal({
  open,
  onClose,
  onSelect,
  onPremiumRequired,
  gameName,
  gameId,
  gameColor,
}: CategorySelectModalProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const categories = getCategoriesForGame(gameId);

  if (!open) return null;

  const handleCategoryClick = (cat: Category) => {
    if (cat.premium) {
      onPremiumRequired();
      return;
    }
    onSelect(cat);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-lg rounded-2xl p-6 animate-bounce-in max-h-[90vh] overflow-y-auto scrollbar-hide"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-bold text-xl leading-tight">Kategorie wählen</h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              {gameName} — Wähle ein Thema
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 gap-3">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryClick(cat)}
              onMouseEnter={() => setHovered(cat.id)}
              onMouseLeave={() => setHovered(null)}
              className="relative flex flex-col items-start p-4 rounded-xl text-left transition-all duration-200"
              style={{
                background: hovered === cat.id && !cat.premium ? `${gameColor}15` : 'var(--muted)',
                border: `1px solid ${hovered === cat.id && !cat.premium ? gameColor + '50' : 'var(--border)'}`,
                opacity: cat.premium ? 0.85 : 1,
              }}
            >
              {cat.premium && (
                <div className="absolute top-2 right-2">
                  <span
                    className="flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)', color: '#fff' }}
                  >
                    <Lock size={8} />
                    PRO
                  </span>
                </div>
              )}
              <span className="text-2xl mb-2">{cat.emoji}</span>
              <span className="font-semibold text-sm leading-tight">{cat.label}</span>
              <span className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                {cat.description}
              </span>
              {cat.premium && (
                <div className="flex items-center gap-1 mt-2">
                  <Sparkles size={10} style={{ color: '#F59E0B' }} />
                  <span className="text-xs font-medium" style={{ color: '#F59E0B' }}>Premium</span>
                </div>
              )}
              {!cat.premium && (
                <ChevronRight
                  size={14}
                  className="absolute bottom-3 right-3 transition-opacity"
                  style={{ color: gameColor, opacity: hovered === cat.id ? 1 : 0 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
